/**
 * Prefix used for UI-generated message IDs.
 * Messages with this prefix are not yet persisted to the database.
 */
export const UiGeneratedIdPrefix = 'ui-generated-';

/**
 * Checks if a message ID was generated on the client side (not persisted).
 *
 * @param id - The message ID to check
 * @returns True if the ID was generated on the client side
 */
export function isUIGeneratedId(id: string | number | undefined): boolean {
  return typeof id === 'string' && id.startsWith(UiGeneratedIdPrefix);
}

/**
 * Determines if a message can receive feedback.
 *
 * A message can receive feedback if it has been persisted to the database,
 * which means it does NOT have a UI-generated ID prefix.
 *
 * @param id - The message ID (string for client-generated, number for persisted, undefined if not set)
 * @returns True if the message can receive feedback
 */
export function isMessagePersistedAndRatable(id: string | number | undefined): boolean {
  if (id === undefined) {
    return false;
  }
  return !isUIGeneratedId(id);
}

/**
 * Determines if a message is the last assistant message in a consecutive sequence.
 *
 * Feedback should only be shown on the last assistant message when there are
 * multiple consecutive assistant messages. A change of `source` (e.g. a chat
 * reply followed by an Atlas digest) ends the sequence — each visually
 * distinct message keeps its own feedback controls.
 *
 * @param messages - Array of messages with isUser and optional source
 * @param index - Index of the message to check
 * @returns True if this is the last assistant message in the sequence
 */
export function isLastAssistantMessageInSequence(
  messages: Array<{ isUser: boolean; source?: string }>,
  index: number
): boolean {
  const message = messages[index];
  if (message.isUser) {
    return false;
  }

  const nextMessage = messages[index + 1];
  return !nextMessage || nextMessage.isUser || nextMessage.source !== message.source;
}
